import { AgentSaleActionTypes } from "models/actionTypes/agentSaleActionTypes";
import { AnnualAccountReportActionTypes } from "models/actionTypes/annualAccountReportActionTypes";
import { BankIdPaymentsActionTypes } from "models/actionTypes/bankIdPaymentsActionTypes";
import { DocumentDraftActionTypes } from "models/actionTypes/documentDraftActionTypes";
import { GenericDocumentActionTypes } from "models/actionTypes/genericDocumentActionTypes";
import { IncomingInvoiceActionType } from "models/actionTypes/incomingInvoiceActionTypes";
import { InvoiceActionType } from "models/actionTypes/invoiceActionTypes";
import { MissingDocumentActionTypes } from "models/actionTypes/missingDocumentActionTypes";
import { PausedPaymentsActionTypes } from "models/actionTypes/pausedPaymentsActionTypes";
import { PersonSalaryActionTypes } from "models/actionTypes/personSalaryActionTypes";
import { ProfileRoutineActionTypes } from "models/actionTypes/profileRoutineActionTypes";
import { QualifiedSharedDividendReportActionTypes } from "models/actionTypes/qualifiedSharedDividendsActionTypes";
import { ReceiptActionType } from "models/actionTypes/receiptActionTypes";
import { RootActionType } from "models/actionTypes/rootActionTypes";
import { SalaryDeviationsActionTypes } from "models/actionTypes/salaryDeviationsActionTypes";
import { SalaryDraftActionTypes } from "models/actionTypes/salaryDraftActionTypes";
import { StoppedPaymentsActionTypes } from "models/actionTypes/stoppedPaymentsActionTypes";
import { SupplementaryMaterialActionTypes } from "models/actionTypes/supplementaryMaterialActionTypes";
import { SupportActionTypes } from "models/actionTypes/supportActionTypes";
import { SurveyActionTypes } from "models/actionTypes/surveyActionTypes";
import { TimeLogsActionTypes } from "models/actionTypes/timeLogsActionTypes";
import { TodoActionType } from "models/actionTypes/todoActionTypes";
import { UnhandledBankPaymentsActionTypes } from "models/actionTypes/unhandledBankPaymentsActionTypes";
import { makeTodoKey, TodoNavigationState, TodoState } from "models/stateTypes/todoState";
import { ReduxConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { Map, Seq, List } from "immutable";
import { IncomingInvoiceService, ReceiptService } from "services";
import { BoardMembersDataActionTypes } from "models/actionTypes/boardMembersDataActionTypes";
import { Loading } from "models/enums/loading";
import { ShareRegisterActionTypes } from "models/actionTypes/shareRegisterActionTypes";
import { SelfInvoiceActionTypes } from "models/actionTypes/selfInvoiceActionTypes";
import { AnnualReportSigningStepsActionTypes } from "models/actionTypes/annualReportSigningStepsActionTypes";
import { AnnualReportAuditorSigningStepsActionTypes } from "models/actionTypes/annualReportAuditorSigningStepsActionTypes";
import { ExternalCompanyHoldingsActionTypes } from "models/actionTypes/externalCompanyHoldingsActionTypes";
import { PreliminaryTaxUpdateActionTypes } from "models/actionTypes/preliminaryTaxUpdateActionTypes";
import { RevenueReportActionTypes } from "models/actionTypes/revenueReportActionTypes";
import { InvoiceHelper } from "utils/helpers";

const TodoGroup = WintSuperkollAPIModel.TodoGroup;
const TodoType = WintSuperkollAPIModel.TodoType;
const TodoSubType = WintSuperkollAPIModel.TodoSubType;
const initialState: TodoState = {
  todoLoadingState: Loading.NotLoaded,
  todoSummaryLoadingState: Loading.NotLoaded,
  showTodos: false,
};
type ReducerActions =
  | TodoActionType
  | InvoiceActionType
  | ReceiptActionType
  | IncomingInvoiceActionType
  | SupportActionTypes
  | DocumentDraftActionTypes
  | AnnualAccountReportActionTypes
  | BankIdPaymentsActionTypes
  | GenericDocumentActionTypes
  | MissingDocumentActionTypes
  | PausedPaymentsActionTypes
  | PersonSalaryActionTypes
  | ProfileRoutineActionTypes
  | QualifiedSharedDividendReportActionTypes
  | SalaryDeviationsActionTypes
  | SalaryDraftActionTypes
  | StoppedPaymentsActionTypes
  | SupplementaryMaterialActionTypes
  | TimeLogsActionTypes
  | UnhandledBankPaymentsActionTypes
  | SurveyActionTypes
  | RootActionType
  | AgentSaleActionTypes
  | BoardMembersDataActionTypes
  | ShareRegisterActionTypes
  | SelfInvoiceActionTypes
  | AnnualReportSigningStepsActionTypes
  | AnnualReportAuditorSigningStepsActionTypes
  | ExternalCompanyHoldingsActionTypes
  | PreliminaryTaxUpdateActionTypes
  | RevenueReportActionTypes;

export const todoReducer = (state: TodoState = initialState, action: ReducerActions): TodoState => {
  switch (action.type) {
    case ReduxConstants.RootActions.RESET:
      return initialState;
    case ReduxConstants.TodoActions.START_LOAD_TODOS:
      return {
        ...state,
        todoLoadingState: Loading.Loading,
      };

    case ReduxConstants.TodoActions.LOAD_TODOS:
      return {
        ...state,
        todoLoadingState: Loading.Success,
        todoCount: action.payload.total,
        items: List(action.payload.items?.map(makeTodoKey)),
        itemMap: Map(action.payload.items?.map((i) => [makeTodoKey(i), i])),
        urgencyMap:
          action.payload.items === undefined
            ? Map()
            : (state.urgencyMap ?? Map()).withMutations((m) => {
                for (const [urgency, items] of Seq(action.payload.items ?? []).groupBy((i) => i.urgency)) {
                  const currentUrgencyGroup = m.get(urgency);
                  m.set(urgency, {
                    total: currentUrgencyGroup?.total ?? items.count(),
                    items: items.map(makeTodoKey).toList(),
                  });
                }
              }),
        groupMap:
          action.payload.items === undefined
            ? Map()
            : (state.groupMap ?? Map()).withMutations((m) => {
                for (const [group, items] of Seq(action.payload.items ?? []).groupBy((i) => i.group)) {
                  const currentGroupGroup = m.get(group);
                  m.set(group, {
                    total: currentGroupGroup?.total ?? items.count(),
                    items: items.map(makeTodoKey).toList(),
                  });
                }
              }),
      };

    case ReduxConstants.TodoActions.EMPTY_TODOS:
      return {
        ...state,
        todoLoadingState: Loading.Success,
        todoCount: undefined,
        items: List(),
        itemMap: Map(),
        urgencyMap: Map(),
        groupMap: Map(),
      };

    case ReduxConstants.TodoActions.LOAD_TODOS_FAILED:
      return {
        ...state,
        todoLoadingState: Loading.Failure,
      };
    case ReduxConstants.TodoActions.SET_TODO_COUNT:
      return {
        ...state,
        todoCount: action.payload,
      };
    case ReduxConstants.TodoActions.SET_TODO_DRAWER_STATE:
      return {
        ...state,
        showTodos: action.payload,
      };
    case ReduxConstants.TodoActions.START_LOAD_TODOS_SUMMARY:
      return {
        ...state,
        todoSummaryLoadingState: Loading.Loading,
      };
    case ReduxConstants.TodoActions.LOAD_TODOS_SUMMARY:
      return {
        ...state,
        todoSummaryLoadingState: Loading.Success,
        todoSummary: action.payload,
        urgencyMap: (state.urgencyMap ?? Map()).withMutations((m) => {
          for (const urgency of action.payload?.todosUrgencySummary ?? []) {
            const urgencyInfo = m.get(urgency.todoUrgency);
            m.set(urgency.todoUrgency, {
              total: urgency.todosCount,
              items: urgencyInfo?.items ?? List(),
            });
          }
        }),
        groupMap: (state.groupMap ?? Map()).withMutations((m) => {
          for (const [group, gs] of Seq(action.payload?.todosGroupSummary ?? []).groupBy((gs) => gs.group)) {
            const groupInfo = m.get(group);
            m.set(group, {
              total: gs.reduce((r, v) => r + v.todosCount, 0),
              items: groupInfo?.items ?? List(),
            });
          }
        }),
      };
    case ReduxConstants.TodoActions.EMPTY_TODOS_SUMMARY:
      return {
        ...state,
        todoSummaryLoadingState: Loading.Success,
        todoSummary: undefined,
        urgencyMap: Map(),
        groupMap: Map(),
      };
    case ReduxConstants.TodoActions.LOAD_TODOS_SUMMARY_FAILED:
      return {
        ...state,
        todoSummaryLoadingState: Loading.Failure,
      };
    case ReduxConstants.TodoActions.LOAD_TODOS_FOR_URGENCY:
      return {
        ...state,
        todoSummary: state.todoSummary
          ? updateTodoSummaryUrgencyTotal(state.todoSummary, action.payload.urgency, action.payload.total)
          : undefined,
        itemMap: (state.itemMap ?? Map()).withMutations((m) => {
          for (const todo of action.payload.items) {
            m.set(makeTodoKey(todo), todo);
          }
        }),
        urgencyMap: (state.urgencyMap ?? Map()).set(action.payload.urgency, {
          total: action.payload.total,
          items: List(action.payload.items.map(makeTodoKey)),
        }),
      };
    case ReduxConstants.TodoActions.LOAD_TODOS_FOR_GROUP:
      return {
        ...state,
        todoSummary: state.todoSummary
          ? updateTodoSummaryGroupTotal(
              state.todoSummary,
              action.payload.group,
              action.payload.total,
              Seq(action.payload.items)
                .groupBy((t) => t.subType)
                .map((ts) => ts.count())
            )
          : undefined,
        itemMap: (state.itemMap ?? Map()).withMutations((m) => {
          for (const todo of action.payload.items) {
            m.set(makeTodoKey(todo), todo);
          }
        }),
        groupMap: (state.groupMap ?? Map()).set(action.payload.group, {
          total: action.payload.total,
          items: List(action.payload.items.map(makeTodoKey)),
        }),
      };
    case ReduxConstants.TodoActions.SET_LAST_VIEWED_TAB:
      return {
        ...state,
        lastViewedTab: action.payload,
      };
    case ReduxConstants.TodoActions.SET_LAST_VIEWED_TYPE:
      return {
        ...state,
        lastViewedType: action.payload,
      };
    // Agent sales
    case ReduxConstants.AgentSaleActions.MISSING_INFORMATION_SNOOZED:
      return removeItem(state, action.payload, TodoType.AgentSaleMissingInformation);
    // Invoices
    case ReduxConstants.InvoiceActions.INVOICE_CREATED:
    case ReduxConstants.InvoiceActions.INVOICE_ASSIGNED_NEW_OWNER_MYSELF:
      return addItem(
        state,
        action.payload,
        TodoType.Invoice,
        "invoice",
        TodoSubType.InvoiceDraft,
        TodoGroup.CustomerInvoice
      );
    case ReduxConstants.InvoiceActions.INVVOICE_DUE_DATE_CHANGED_TO_PAST_DATE:
      return addItem(
        state,
        action.payload,
        TodoType.Invoice,
        "invoice",
        TodoSubType.InvoiceOverdue,
        TodoGroup.CustomerInvoice
      );
    case ReduxConstants.InvoiceActions.INVOICE_UPDATED:
      return updateItem(
        state,
        action.payload,
        TodoType.Invoice,
        "invoice",
        InvoiceHelper.getTodoSubType(action.payload.status)
      );
    case ReduxConstants.InvoiceActions.INVOICE_UPDATED_CONTENT:
      return updateItem(state, action.payload, TodoType.Invoice, "invoice");
    case ReduxConstants.InvoiceActions.INVOICE_SENT:
    case ReduxConstants.InvoiceActions.INVOICE_DISCARDED:
    case ReduxConstants.InvoiceActions.INVOICE_DUE_DATE_CHANGED_TO_FUTURE_DATE:
    case ReduxConstants.InvoiceActions.INVOICE_SNOOZED:
    case ReduxConstants.InvoiceActions.INVOICE_REMINDER_FEE_WRITTEN_OFF:
    case ReduxConstants.InvoiceActions.INVOICE_CREDITED:
    case ReduxConstants.InvoiceActions.INVOICE_ASSIGNED_NEW_OWNER_OTHERS:
      return removeItem(state, action.payload, TodoType.Invoice);
    // Self Invoice
    case ReduxConstants.SelfInvoiceActions.SELFINVOICE_SENT:
    case ReduxConstants.SelfInvoiceActions.SELFINVOICE_DISCARDED:
    case ReduxConstants.SelfInvoiceActions.SELFINVOICE_SNOOZED:
      return removeItem(state, action.payload, TodoType.SelfInvoice);
    case ReduxConstants.SelfInvoiceActions.SELFINVOICE_SAVED:
      return updateItem(state, action.payload, TodoType.SelfInvoice, "selfInvoice");
    // Receipts
    case ReduxConstants.ReceiptActions.RECEIPT_UPDATED:
      return updateItem(
        state,
        action.payload,
        TodoType.Receipt,
        "receipt",
        ReceiptService.getReceiptTodoSubType(action.payload.state, action.payload.sendBackData?.message),
        true
      );
    case ReduxConstants.ReceiptActions.RECEIPT_UPDATED_CONTENT:
      return updateItem(state, action.payload, TodoType.Receipt, "receipt");
    case ReduxConstants.ReceiptActions.RECEIPT_SENT_BACK:
    case ReduxConstants.ReceiptActions.RECEIPT_APPROVED:
    case ReduxConstants.ReceiptActions.RECEIPT_REMOVED:
    case ReduxConstants.ReceiptActions.RECEIPT_SNOOZED:
      return removeItem(state, action.payload, TodoType.Receipt);
    // Receipt - drafts
    case ReduxConstants.ReceiptActions.RECEIPT_DRAFT_SEND_IN:
    case ReduxConstants.ReceiptActions.RECEIPT_DRAFT_REMOVED:
      return removeItem(state, action.payload, TodoType.ReceiptDraft);
    case ReduxConstants.ReceiptActions.RECEIPT_DRAFT_UPDATED:
      return updateItem(state, action.payload, TodoType.ReceiptDraft, "receipt");
    case ReduxConstants.ReceiptActions.RECEIPT_CREATED_FROM_CLASSIFICATION:
      return removeItem(state, action.payload, TodoType.ClassifiedReceiptTodo);
    case ReduxConstants.ReceiptActions.DELETE_CLASSIFIED_RECEIPT:
      return removeItem(state, action.payload, TodoType.ClassifiedReceiptTodo);
    case ReduxConstants.ReceiptActions.CONVERT_CLASSIFIED_RECEIPT:
      return removeItem(state, action.payload, TodoType.ClassifiedReceiptTodo);
    // Incoming invoices
    case ReduxConstants.IncomingInvoiceActions.INCOMING_INVOICE_UPDATED:
      return updateItem(
        state,
        action.payload.invoice,
        TodoType.IncomingInvoice,
        "incomingInvoice",
        IncomingInvoiceService.getTodoSubType(
          action.payload.invoice.state,
          action.payload.loggedInUserId,
          action.payload.permissions,
          action.payload.invoice.certifyPersonId
        ),
        true
      );
    case ReduxConstants.IncomingInvoiceActions.INCOMING_INVOICE_UPDATED_CONTENT:
      return updateItem(state, action.payload, TodoType.IncomingInvoice, "incomingInvoice");

    case ReduxConstants.IncomingInvoiceActions.INCOMING_INVOICE_ACCEPTED:
    case ReduxConstants.IncomingInvoiceActions.INCOMING_INVOICE_FORWARDED:
    case ReduxConstants.IncomingInvoiceActions.INCOMING_INVOICE_SENT_BACK:
    case ReduxConstants.IncomingInvoiceActions.INCOMING_INVOICE_CONVERTED:
    case ReduxConstants.IncomingInvoiceActions.INCOMING_INVOICE_CANCELLED:
    case ReduxConstants.IncomingInvoiceActions.INCOMING_INVOICE_SNOOZED:
      return removeItem(state, action.payload, TodoType.IncomingInvoice);
    case ReduxConstants.IncomingInvoiceActions.AGING_INCOMING_INVOICE_SUBMITTED:
    case ReduxConstants.IncomingInvoiceActions.AGING_INCOMING_INVOICE_SNOOZED:
      return removeItem(state, action.payload, TodoType.AgingIncomingInvoice);
    // Support issues
    case ReduxConstants.SupportActions.SUPPORT_COMMENT_ADDED:
    case ReduxConstants.SupportActions.SUPPORT_RESOLVED:
    case ReduxConstants.SupportActions.SUPPORT_CONFIRM_RESOLVED:
    case ReduxConstants.SupportActions.SUPPORT_RESPONSE_SUBMITTED:
    case ReduxConstants.SupportActions.SUPPORT_FORWARDED:
    case ReduxConstants.SupportActions.SUPPORT_SNOOZED:
      return removeItem(state, action.payload, TodoType.Support);
    case ReduxConstants.SupportActions.SUPPORT_RATED:
      return updateItem(state, action.payload, TodoType.Support, "supportIssue");
    // Document drafts
    case ReduxConstants.DocumentDraftActions.DOCUMENT_DRAFT_HANDLED:
    case ReduxConstants.DocumentDraftActions.DOCUMENT_DRAFT_DELETED:
    case ReduxConstants.DocumentDraftActions.DOCUMENT_DRAFT_SNOOZED:
      return removeItem(state, action.payload, TodoType.DocumentDraft);
    // Annual account report
    case ReduxConstants.AnnualAccountReportActions.ANNUAL_ACCOUNT_REPORT_ACCEPTED:
    case ReduxConstants.AnnualAccountReportActions.ANNUAL_ACCOUNT_REPORT_REJECTED:
    case ReduxConstants.AnnualAccountReportActions.ANNUAL_ACCOUNT_REPORT_COMMENTED:
    case ReduxConstants.AnnualAccountReportActions.ANNUAL_ACCOUNT_REPORT_SNOOZED:
      return removeItem(state, action.payload, TodoType.AnnualAccountsForCustomerApproval);
    // Board members data
    case ReduxConstants.BoardMembersDataActions.BOARD_MEMBERS_DATA_SNOOZED:
      return removeItem(state, action.payload, TodoType.UpdateNewBoardMembersData);
    case ReduxConstants.BoardMembersDataActions.BOARD_MEMBERS_DATA_UPDATED:
      return removeItem(state, 0, TodoType.UpdateNewBoardMembersData);
    // Annual report signing steps at oneflow
    case ReduxConstants.AnnualReportSigningStepsActions.ANNUAL_REPORT_STEP_SIGNED:
      return removeItem(state, action.payload, TodoType.AnnualReportSigningSteps);
    // Annual report auditor signing steps
    case ReduxConstants.AnnualReportAuditorSigningStepsActions.ANNUAL_REPORT_AUDITOR_STEP_SIGNED:
      return removeItem(state, action.payload, TodoType.AuditorAnnualReportSigningSteps);
    // Bank Id payments
    case ReduxConstants.BankIdPaymentsActions.BANK_ID_PAYMENTS_REPORT_UPDATED:
      return updateItem(
        state,
        { ...action.payload, id: 0 },
        TodoType.PaymentSigningInformation,
        "paymentSigningInformation",
        undefined,
        true
      );
    case ReduxConstants.BankIdPaymentsActions.BANK_ID_PAYMENTS_REPORT_COMPLETED:
      return removeItem(state, action.payload, TodoType.PaymentSigningInformation);
    // Generic documents
    case ReduxConstants.GenericDocumentActions.GENERIC_DOCUMENT_SENT_BACK_APPROVED:
    case ReduxConstants.GenericDocumentActions.GENERIC_DOCUMENT_SENT_BACK_SNOOZED:
      return removeItem(state, action.payload, TodoType.GenericVouchersSentBack);
    case ReduxConstants.GenericDocumentActions.GENERIC_DOCUMENT_SENT_BACK_UPDATED:
      return updateItem(state, action.payload, TodoType.GenericVouchersSentBack, "genericVoucherSendBack");
    // Missing documents
    case ReduxConstants.MissingDocumentActions.MISSING_DOCUMENT_ACCEPTED:
    case ReduxConstants.MissingDocumentActions.MISSING_DOCUMENT_SNOOZED:
      return removeItem(state, action.payload, TodoType.MissingDocument);
    case ReduxConstants.MissingDocumentActions.MISSING_DOCUMENT_UPDATED:
    case ReduxConstants.MissingDocumentActions.MISSING_DOCUMENT_UPDATED_CONTENT:
      return updateItem(state, action.payload, TodoType.MissingDocument, "missingDocument");
    // Paused payments
    case ReduxConstants.PausedPaymentsActions.PAUSED_PAYMENTS_REPORT_UPDATED:
      return updateItem(
        state,
        { ...action.payload, id: 0 },
        TodoType.PausedPayments,
        "pausedPayments",
        undefined,
        true
      );
    case ReduxConstants.PausedPaymentsActions.PAUSED_PAYMENTS_REPORT_COMPLETED:
      return removeItem(state, action.payload, TodoType.PausedPayments);
    // Person salary
    case ReduxConstants.PersonSalaryActions.PERSON_SALARY_REPORT_UPDATED:
      return updateItem(state, { ...action.payload, id: 0 }, TodoType.PersonSalaryReport, "personSalaryReport");
    case ReduxConstants.PersonSalaryActions.PERSON_SALARY_REPORT_COMPLETED:
      return removeItem(state, action.payload, TodoType.PersonSalaryReport);
    // Profile routines
    case ReduxConstants.ProfileRoutineActions.PROFILE_ROUTINE_APPROVED:
    case ReduxConstants.ProfileRoutineActions.PROFILE_ROUTINE_SNOOZED:
      return removeItem(state, action.payload, TodoType.ProfileRoutine);
    // Qualified share dividends
    case ReduxConstants.QualifiedSharedDividendsActions.QUALIFIED_SHARED_DIVIDENDS_REPORT_ACCEPTED:
    case ReduxConstants.QualifiedSharedDividendsActions.QUALIFIED_SHARED_DIVIDENDS_REPORT_REJECTED:
    case ReduxConstants.QualifiedSharedDividendsActions.QUALIFIED_SHARED_DIVIDENDS_REPORT_SNOOZED:
      return removeItem(state, action.payload, TodoType.QualifiedShareDividend);
    // Salary deviations
    case ReduxConstants.SalaryDeviationsActions.SALARY_DEVIATIONS_SUBMITTED:
      return removeItem(state, action.payload, TodoType.SalaryDeviationReport);
    case ReduxConstants.SalaryDeviationsActions.SALARY_DEVIATIONS_WITH_BUSINESSUNITS_SUBMITTED:
      return removeItem(state, action.payload, TodoType.SalaryDeviationReportWithBusinessUnits);
    // Wint salary
    case ReduxConstants.SalaryDeviationsActions.WINT_SALARY_APPROVAL_REPORT_SUBMITTED:
      return removeItem(state, action.payload, TodoType.WintSalaryApprovalReport);
    // Salary draft
    case ReduxConstants.SalaryDraftActions.SALARY_DRAFT_ACCEPTED:
    case ReduxConstants.SalaryDraftActions.SALARY_DRAFT_REJECTED:
    case ReduxConstants.SalaryDraftActions.SALARY_DRAFT_FORWARDED:
      return removeItem(state, action.payload, TodoType.SalaryDraft);
    // Stopped payments
    case ReduxConstants.StoppedPaymentsActions.STOPPED_PAYMENTS_REPORT_UPDATED:
      return updateItem(
        state,
        { ...action.payload, id: 0 },
        TodoType.StoppedPayment,
        "stoppedPayments",
        undefined,
        true
      );
    case ReduxConstants.StoppedPaymentsActions.STOPPED_PAYMENTS_REPORT_COMPLETED:
      return removeItem(state, action.payload, TodoType.StoppedPayment);
    // Supplementary material
    case ReduxConstants.SupplementaryMaterialActions.SUPPLEMENTARY_MATERIAL_ACCEPTED:
    case ReduxConstants.SupplementaryMaterialActions.SUPPLEMENTARY_MATERIAL_SNOOZED:
      return removeItem(state, action.payload, TodoType.SupplementaryMaterial);
    // Time logs
    case ReduxConstants.TimeLogsActions.TIME_LOGS_APPROVED:
      return removeItem(state, action.payload, TodoType.SalaryDeviationAndTimeLogReport);
    // Unhandled bank payments
    case ReduxConstants.UnhandledBankPaymentsActions.UNHANDLED_BANK_PAYMENTS_REPORT_UPDATED:
      return updateItem(
        state,
        { ...action.payload, id: 0 },
        TodoType.UnhandledBankPayments,
        "unhandledBankPayments",
        undefined,
        true
      );
    case ReduxConstants.UnhandledBankPaymentsActions.UNHANDLED_BANK_PAYMENTS_REPORT_COMPLETED:
      return removeItem(state, action.payload, TodoType.UnhandledBankPayments);
    // Survey
    case ReduxConstants.SurveyActions.SURVEY_UPDATED:
      return updateItem(state, { ...action.payload, id: action.payload.surveyId }, TodoType.Survey, "surveyStatus");
    case ReduxConstants.SurveyActions.SURVEY_SUBMITTED:
    case ReduxConstants.SurveyActions.SURVEY_FORWARDED:
    case ReduxConstants.SurveyActions.SURVEY_SNOOZED:
      return removeItem(state, action.payload, TodoType.Survey);
    // Share register Confirmation
    case ReduxConstants.ShareRegisterActions.SHARE_REGISTER_ACCEPTED:
    case ReduxConstants.ShareRegisterActions.SHARE_REGISTER_REJECTED:
    case ReduxConstants.ShareRegisterActions.SHARE_REGISTER_SNOOZED:
      return removeItem(state, action.payload, TodoType.ShareRegister);
    // Removed document draft todo
    case ReduxConstants.DocumentDraftActions.DOCUMENT_DRAFT_REMOVED_HANDLED:
      return removeItem(state, action.payload, TodoType.RemovedDocumentDraft);
    // External company holdings
    case ReduxConstants.ExternalCompanyHoldingsActions.EXTERNAL_COMPANY_HOLDINGS_APPROVED:
    case ReduxConstants.ExternalCompanyHoldingsActions.EXTERNAL_COMPANY_HOLDINGS_SNOOZED:
      return removeItem(state, action.payload, TodoType.ExternalCompanyHoldings);
    // Preliminary tax update
    case ReduxConstants.PreliminaryTaxUpdateActions.PRELIMINARY_TAX_UPDATE_SUBMITTED:
      return removeItem(state, action.payload, TodoType.PreliminaryTaxUpdate);
    // Revenue report
    case ReduxConstants.RevenueReportActions.REVENUE_REPORT_APPROVED:
    case ReduxConstants.RevenueReportActions.REVENUE_REPORT_SNOOZED:
      return removeItem(state, action.payload, TodoType.RevenueReportNotSubmitted);
    case ReduxConstants.TodoActions.SET_LIST_NAVIGATION:
      return {
        ...state,
        todoNavigationState: action.payload,
      };
    case ReduxConstants.TodoActions.LOAD_CURRENT_NAVIGATION_ITEMS:
      return {
        ...state,
        todoNavigationState: {
          ...state.todoNavigationState,
          currentNavigationItems: action.payload?.items,
          totalItems: action.payload?.totalItems,
        },
      };
    case ReduxConstants.TodoActions.REFRESH_CURRENT_NAVIGATION_ITEMS:
      return state.todoNavigationState === undefined
        ? state
        : {
            ...state,
            todoNavigationState: {
              ...state.todoNavigationState,
              refreshRequestId: (state.todoNavigationState.refreshRequestId ?? 0) + 1,
            },
          };
    case ReduxConstants.TodoActions.SET_CURRENT_INDEX:
      return {
        ...state,
        todoNavigationState: {
          ...state.todoNavigationState,
          indexToOpen: action.payload.index,
          pageToOpen: action.payload.page,
        },
      };
    default:
      return state;
  }
};

function addItem(
  state: TodoState,
  item: any,
  type: WintSuperkollAPIModel.TodoType,
  propertyName: keyof WintSuperkollAPIModel.Todo,
  subType: WintSuperkollAPIModel.TodoSubType,
  group: WintSuperkollAPIModel.TodoGroup
): TodoState {
  const newTodoItem: WintSuperkollAPIModel.Todo = {
    id: item.id,
    snoozed: false,
    date: new Date(),
    type,
    urgency: WintSuperkollAPIModel.TodoUrgency.ToHandle,
    genericCommentsAvailable: false,
    group: group,
    subType: subType,
    [propertyName]: item,
  };
  const newTodoKey = makeTodoKey(newTodoItem);
  const newItemMap = (state.itemMap ?? Map()).set(newTodoKey, newTodoItem);
  const newItems = (state.items ?? List()).push(newTodoKey);
  const totalCount = state.todoCount ? state.todoCount + 1 : 1;

  const urgencyInfo = state.urgencyMap?.get(WintSuperkollAPIModel.TodoUrgency.ToHandle) ?? { total: 0, items: List() };
  const newUrgencyMap = (state.urgencyMap ?? Map()).set(WintSuperkollAPIModel.TodoUrgency.ToHandle, {
    total: urgencyInfo.total + 1,
    items: urgencyInfo.items.push(newTodoKey),
  });

  const groupInfo = state.groupMap?.get(group) ?? { total: 0, items: List() };
  const newGroupMap = (state.groupMap ?? Map()).set(group, {
    total: groupInfo.total + 1,
    items: groupInfo.items.push(newTodoKey),
  });

  const todoSummary = getTodoSummary(state);

  if (todoSummary) {
    const typeIndex = todoSummary.todosTypeSummary?.findIndex((t) => t.type === type);
    if (typeIndex !== undefined && typeIndex !== -1) {
      todoSummary.todosTypeSummary![typeIndex].todosCount++;
    } else {
      todoSummary.todosTypeSummary?.push({
        type,
        todosCount: 1,
      });
    }

    const urgencyIndex = todoSummary.todosUrgencySummary?.findIndex(
      (t) => t.todoUrgency === WintSuperkollAPIModel.TodoUrgency.ToHandle
    );
    if (urgencyIndex !== undefined && urgencyIndex !== -1) {
      todoSummary.todosUrgencySummary![urgencyIndex].todosCount++;
    } else {
      todoSummary.todosUrgencySummary?.push({
        todoUrgency: WintSuperkollAPIModel.TodoUrgency.ToHandle,
        todosCount: 1,
      });
    }

    const groupIndex = todoSummary.todosGroupSummary?.findIndex(
      (t) => t.group === newTodoItem.group && t.subType === newTodoItem.subType
    );
    if (groupIndex !== undefined && groupIndex !== -1) {
      todoSummary.todosGroupSummary![groupIndex].todosCount++;
    } else {
      todoSummary.todosGroupSummary?.push({
        group: newTodoItem.group,
        subType: newTodoItem.subType,
        todosCount: 1,
      });
    }

    todoSummary.totalTodosCount++;
  }
  return {
    ...state,
    items: newItems,
    itemMap: newItemMap,
    urgencyMap: newUrgencyMap,
    groupMap: newGroupMap,
    todoCount: totalCount,
    todoSummary,
    todoNavigationState: addItemInNavigationState(state, newTodoItem),
  };
}

function addItemInNavigationState(
  state: TodoState,
  newTodoItem: WintSuperkollAPIModel.Todo
): TodoNavigationState | undefined {
  if (
    state.todoNavigationState?.todoCanIncludeInNavigation &&
    state.todoNavigationState?.todoCanIncludeInNavigation(newTodoItem)
  ) {
    const newItems = [...(state.todoNavigationState!.currentNavigationItems ?? [])];
    let newNumPerPage = state.todoNavigationState?.numPerPage ?? 0;
    const total = state.todoNavigationState?.totalItems ?? 0;
    const newTotal = total + 1;
    const lastPage = Math.ceil(newTotal / newNumPerPage) - 1;

    if (newNumPerPage >= total || lastPage === state.todoNavigationState!.pageToOpen) {
      newItems.push(newTodoItem);

      if (newNumPerPage >= total) {
        newNumPerPage = newItems.length;
      }
    }

    return {
      ...state.todoNavigationState,
      currentNavigationItems: newItems,
      totalItems: newTotal,
      numPerPage: newNumPerPage,
    };
  }

  return state.todoNavigationState ? { ...state.todoNavigationState } : undefined;
}

function updateItem(
  state: TodoState,
  item: any,
  type: WintSuperkollAPIModel.TodoType,
  propertyName: keyof WintSuperkollAPIModel.Todo,
  subType?: WintSuperkollAPIModel.TodoSubType,
  urgencyChanged?: boolean
): TodoState {
  const todoKey = makeTodoKey({
    id: item.id,
    guidId: typeof item.id === "string" ? item.id : undefined,
    type,
  } as WintSuperkollAPIModel.Todo);
  const oldItem = state.itemMap?.get(todoKey);
  if (oldItem) {
    let todoSummary: WintSuperkollAPIModel.TodoSummary | undefined = undefined;
    if (subType !== undefined && subType !== oldItem.subType) {
      todoSummary = getTodoSummary(state);

      if (todoSummary) {
        const typeIndex = todoSummary.todosTypeSummary?.findIndex((t) => t.type === type);
        if (typeIndex !== undefined && typeIndex !== -1) {
          todoSummary.todosTypeSummary![typeIndex].todosCount--;
        }
        const groupIndexOld = todoSummary.todosGroupSummary?.findIndex(
          (t) => t.group === oldItem.group && t.subType === oldItem.subType
        );
        if (groupIndexOld !== undefined && groupIndexOld !== -1) {
          if (todoSummary.todosGroupSummary![groupIndexOld].todosCount === 1)
            todoSummary.todosGroupSummary!.splice(groupIndexOld, 1);
          else todoSummary.todosGroupSummary![groupIndexOld].todosCount--;
        }
        const groupIndexNew = todoSummary.todosGroupSummary?.findIndex(
          (t) => t.group === oldItem.group && t.subType === subType
        );
        if (groupIndexNew !== undefined && groupIndexNew !== -1) {
          todoSummary.todosGroupSummary![groupIndexNew].todosCount++;
        } else {
          todoSummary.todosGroupSummary?.push({
            group: oldItem.group,
            subType: subType,
            todosCount: 1,
          });
        }
      }
    }

    return {
      ...state,
      todoNavigationState: updateItemInNavigationState(state, item, type, propertyName, subType, urgencyChanged),
      itemMap: state.itemMap?.set(todoKey, {
        ...oldItem,
        [propertyName]: item,
        subType: subType ?? oldItem.subType,
        urgencyChanged: urgencyChanged,
      }),
      todoSummary: todoSummary ?? state.todoSummary,
    };
  }
  return {
    ...state,
    todoNavigationState: updateItemInNavigationState(state, item, type, propertyName),
  };
}

function updateItemInNavigationState(
  state: TodoState,
  item: any,
  type: WintSuperkollAPIModel.TodoType,
  propertyName: keyof WintSuperkollAPIModel.Todo,
  subType?: WintSuperkollAPIModel.TodoSubType,
  urgencyChanged?: boolean
): TodoNavigationState | undefined {
  const todoIndex = state.todoNavigationState?.currentNavigationItems?.findIndex(
    (t) =>
      t.type === type &&
      ((typeof item.id === "number" && t.id === item.id) || (typeof item.id === "string" && t.guidId === item.id))
  );

  if (todoIndex !== undefined && todoIndex !== -1) {
    const newItems = [...(state.todoNavigationState!.currentNavigationItems ?? [])];
    const oldItem = newItems[todoIndex];
    const newItem = {
      ...oldItem,
      [propertyName]: item,
      subType: subType ?? oldItem.subType,
      urgencyChanged: urgencyChanged,
    };

    if (
      state.todoNavigationState?.todoCanIncludeInNavigation &&
      state.todoNavigationState?.todoCanIncludeInNavigation(newItem)
    ) {
      newItems[todoIndex] = newItem;
      return { ...state.todoNavigationState, currentNavigationItems: newItems };
    } else {
      return removeItemFromNavigation(state, item.id, type);
    }
  }

  return state.todoNavigationState ? { ...state.todoNavigationState } : undefined;
}

function removeItem(state: TodoState, id: number | string, type: WintSuperkollAPIModel.TodoType): TodoState {
  const todoKey = makeTodoKey({
    id: typeof id === "number" ? id : 0,
    guidId: typeof id === "string" ? id : undefined,
    type,
  } as WintSuperkollAPIModel.Todo);
  const todoItem = state.itemMap?.get(todoKey);
  const todoIndex = state.items?.indexOf(todoKey) ?? -1;
  if (todoItem) {
    const newItems = todoIndex !== -1 ? state.items?.delete(todoIndex) : state.items;
    const newItemMap = state.itemMap?.delete(todoKey);
    let newUrgencyMap = state.urgencyMap;
    const urgencyInfo = newUrgencyMap?.get(todoItem.urgency);
    if (urgencyInfo) {
      const urgencyIndex = urgencyInfo.items.indexOf(todoKey);
      if (urgencyIndex !== -1) {
        const newTotalUrgency = urgencyInfo.total - 1;
        const newUrgencyItems = urgencyInfo.items.delete(urgencyIndex);

        if (newTotalUrgency === 0) {
          newUrgencyMap = newUrgencyMap?.delete(todoItem.urgency);
        } else {
          newUrgencyMap = newUrgencyMap?.set(todoItem.urgency, {
            total: newTotalUrgency,
            items: newUrgencyItems,
          });
        }
      }
    }
    let newGroupMap = state.groupMap;
    const groupInfo = newGroupMap?.get(todoItem.group);
    if (groupInfo) {
      const groupIndex = groupInfo.items.indexOf(todoKey);
      if (groupIndex !== -1) {
        const newTotal = groupInfo.total - 1;
        const newItems = groupInfo.items.delete(groupIndex);

        if (newTotal === 0) {
          newGroupMap = newGroupMap?.delete(todoItem.group);
        } else {
          newGroupMap = newGroupMap?.set(todoItem.group, {
            total: newTotal,
            items: newItems,
          });
        }
      }
    }
    const todoSummary = getTodoSummary(state);
    if (todoSummary) {
      const typeIndex = todoSummary.todosTypeSummary?.findIndex((t) => t.type === type);
      if (typeIndex !== undefined && typeIndex !== -1) {
        todoSummary.todosTypeSummary![typeIndex].todosCount--;
      }
      const urgencyIndex = todoSummary.todosUrgencySummary?.findIndex((t) => t.todoUrgency === todoItem.urgency);
      if (urgencyIndex !== undefined && urgencyIndex !== -1) {
        if (todoSummary.todosUrgencySummary![urgencyIndex].todosCount === 1)
          todoSummary.todosUrgencySummary!.splice(urgencyIndex, 1);
        else todoSummary.todosUrgencySummary![urgencyIndex].todosCount--;
      }
      const groupIndex = todoSummary.todosGroupSummary?.findIndex(
        (t) => t.group === todoItem.group && t.subType === todoItem.subType
      );
      if (groupIndex !== undefined && groupIndex !== -1) {
        if (todoSummary.todosGroupSummary![groupIndex].todosCount === 1)
          todoSummary.todosGroupSummary!.splice(groupIndex, 1);
        else todoSummary.todosGroupSummary![groupIndex].todosCount--;
      }
      todoSummary.totalTodosCount--;
    }

    return {
      ...state,
      todoNavigationState: removeItemFromNavigation(state, id, type),
      todoCount: state.todoCount! - 1,
      items: newItems,
      itemMap: newItemMap,
      urgencyMap: newUrgencyMap,
      groupMap: newGroupMap,
      todoSummary: todoSummary,
    };
  }

  return {
    ...state,
    todoNavigationState: removeItemFromNavigation(state, id, type),
  };
}

function removeItemFromNavigation(
  state: TodoState,
  id: number | string,
  type: WintSuperkollAPIModel.TodoType
): TodoNavigationState | undefined {
  const todoIndex = state.todoNavigationState?.currentNavigationItems?.findIndex(
    (t) => t.type === type && ((typeof id === "number" && t.id === id) || (typeof id === "string" && t.guidId === id))
  );

  if (todoIndex !== undefined && todoIndex !== -1) {
    const newItems = [...state.todoNavigationState!.currentNavigationItems!];
    newItems.splice(todoIndex!, 1);

    return {
      ...state.todoNavigationState,
      currentNavigationItems: newItems,
      totalItems: (state.todoNavigationState!.totalItems ?? 0) - 1,
    };
  }

  return state.todoNavigationState ? { ...state.todoNavigationState } : undefined;
}

function getTodoSummary(state: TodoState): WintSuperkollAPIModel.TodoSummary | undefined {
  return state.todoSummary
    ? {
        todosTypeSummary: state.todoSummary.todosTypeSummary ? [...state.todoSummary.todosTypeSummary] : undefined,
        todosUrgencySummary: state.todoSummary.todosUrgencySummary
          ? [...state.todoSummary.todosUrgencySummary]
          : undefined,
        todosGroupSummary: state.todoSummary.todosGroupSummary ? [...state.todoSummary.todosGroupSummary] : undefined,
        totalTodosCount: state.todoSummary.totalTodosCount,
      }
    : undefined;
}

function updateTodoSummaryUrgencyTotal(
  todoSummary: WintSuperkollAPIModel.TodoSummary,
  urgency: WintSuperkollAPIModel.TodoUrgency,
  newTotal: number
): WintSuperkollAPIModel.TodoSummary {
  const newTodoSummary = {
    todosTypeSummary: todoSummary.todosTypeSummary ? [...todoSummary.todosTypeSummary] : undefined,
    todosUrgencySummary: todoSummary.todosUrgencySummary ? [...todoSummary.todosUrgencySummary] : undefined,
    todosGroupSummary: todoSummary.todosGroupSummary ? [...todoSummary.todosGroupSummary] : undefined,
    totalTodosCount: todoSummary.totalTodosCount,
  };

  const urgencyIndex = newTodoSummary.todosUrgencySummary?.findIndex((t) => t.todoUrgency === urgency);
  if (urgencyIndex !== undefined && urgencyIndex !== -1) {
    const oldTotal = newTodoSummary.todosUrgencySummary![urgencyIndex].todosCount;
    newTodoSummary.todosUrgencySummary![urgencyIndex].todosCount = newTotal;
    newTodoSummary.totalTodosCount += newTotal - oldTotal;
  }

  return newTodoSummary;
}

function updateTodoSummaryGroupTotal(
  todoSummary: WintSuperkollAPIModel.TodoSummary,
  group: WintSuperkollAPIModel.TodoGroup,
  newTotal: number,
  subTypeTotals: Map<WintSuperkollAPIModel.TodoSubType, number>
): WintSuperkollAPIModel.TodoSummary {
  const newTodoSummary = {
    todosTypeSummary: todoSummary.todosTypeSummary ? [...todoSummary.todosTypeSummary] : undefined,
    todosUrgencySummary: todoSummary.todosUrgencySummary ? [...todoSummary.todosUrgencySummary] : undefined,
    todosGroupSummary: todoSummary.todosGroupSummary ? [...todoSummary.todosGroupSummary] : undefined,
    totalTodosCount: todoSummary.totalTodosCount,
  };

  const groupEntries = newTodoSummary.todosGroupSummary?.filter((t) => t.group === group);

  if (!groupEntries) {
    newTodoSummary.todosGroupSummary = subTypeTotals
      .map((todosCount, subType) => ({ todosCount, subType, group }))
      .toList()
      .toArray();
  } else {
    for (const groupEntry of groupEntries) {
      const newSubTypeTotal = subTypeTotals.get(groupEntry.subType, 0);
      const oldSubTypeTotal = groupEntry.todosCount;
      groupEntry.todosCount = newSubTypeTotal;
      newTodoSummary.totalTodosCount += newSubTypeTotal - oldSubTypeTotal;
    }
  }

  return newTodoSummary;
}
